import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, GraduationCap, Building2, Users, Rocket, Loader2, CheckCircle2, ArrowRight,
  Lightbulb, LineChart, ShoppingBag, IndianRupee, Handshake, Truck, Layers, Bot,
  Brain, ShieldCheck, Factory, BarChart3, MessagesSquare, ChevronRight, Trophy,
  Beaker, ClipboardCheck, Award, Compass, Zap, Target, BookOpen, PenTool, GitBranch,
  UserCog, PlayCircle, Calendar, Timer, Send, X, Download, FileText, HelpCircle,
} from 'lucide-react';
import PoPShell from './PoPShell';
import { usePoPAuth } from '../../context/PoPAuthContext';
import {
  popMBASubmitInterest, popMBAStats, popMBADownloadProspectus,
  popListBooks, popBookCoverUrl,
} from '../../lib/popApi';
import { Reveal, CountUp, AnimatedGradientBg } from '../../components/pop/motion';
import { AdvisorFAB, CohortCountdown } from '../../components/pop/engagement';
import {
  FAQAccordion, CurriculumExplorer, FormatFitQuiz, CapstoneGallery, MultiStepInterest,
  CertificationAlignment,
} from '../../components/pop/mbaContent';

// Two hand-picked reference books to feature on the AI-Enabled MBA hero.
// Matching is case-insensitive substring on the book title.
const REFERENCE_BOOK_TITLES = ['AI ENABLED MBA', 'MANAGEMENT INTELLIGENCE'];

// Next cohort start — Founding cohort begins Q4 2026. Update as cohort dates firm up.
// The <CohortCountdown> auto-hides once the target date is past.
const NEXT_COHORT_ISO = '2026-11-15T09:00:00+05:30';

const MBA_FAQ = [
  {
    q: 'Who is this program actually for?',
    a: 'Working professionals (5-20 years) preparing to lead AI initiatives, mid-career leaders eyeing a functional pivot, entrepreneurs building AI-native ventures, and academic institutions looking to co-teach or license the curriculum. Also open to fresh MBAs seeking a step-up credential.',
  },
  {
    q: 'How is this different from a traditional MBA?',
    a: 'Traditional MBAs teach management functions in silos. The SAMANVAI AI-Enabled MBA integrates AI into every function — marketing, ops, finance, HR — and closes with a real capstone that ships to a live business owner. You leave with prototypes and a portfolio, not just case notes.',
  },
  {
    q: 'How much time will I actually spend?',
    a: 'Fast Track: ~10–15 hours/week across 12 weeks (evenings + weekends, with 2 immersion Saturdays). Full-Time: 24 months, four semesters, immersive. Try the "Which format is right for me?" quiz to get a personalised answer.',
  },
  {
    q: 'Do I need a technical background?',
    a: 'No. Roughly 60% of our founding cohort come from non-technical roles — finance, sales, HR, operations. The curriculum meets you at business-decision level; no code required, though optional labs are available for the curious.',
  },
  {
    q: 'What credential do I walk away with?',
    a: 'A 4-level certification ladder — Foundation → Functional → Enterprise → Capstone. Full completers earn the "AI-Enabled MBA Fellow" title. When licensed by partner universities, we co-issue a hybrid credential with the partner institution. On top of that, every module is aligned to a globally-recognised body of knowledge (PMI, IIBA, ICMCI, IAPP, ISACA, ASCM, CFA, SHRM, Prosci) so graduates can sit the external certifications — PMP, CBAP, CMC, AIGP, CSCP and more — with bridge prep support from our Learner Success Kit.',
  },
  {
    q: 'Which global certifications does this MBA prepare me for?',
    a: 'The curriculum is engineered so every module doubles as exam-runway for one or more portable credentials: PMI (PMP · PMI-ACP · Disciplined Agile · PfMP · PMI-PBA), IIBA (ECBA · CCBA · CBAP · CBDA), ICMCI / IMCI (CMC · AMC — Management Consulting), IAPP (AIGP · CIPP/E · CDPSE), ISACA (CGEIT · CRISC), ASCM/APICS (CSCP · CPIM · CLTD), CFA Institute (CFA Level I), GARP (FRM Part I), SHRM (SHRM-CP / SHRM-SCP), CIPD (Level 5 bridge) and Prosci / APMG for change management. See the “Global Certification Alignment” section for the full mapping.',
  },
  {
    q: 'How are capstones different from generic MBA projects?',
    a: 'Every capstone is briefed by a real business owner — not a case book. You ship a working prototype, a business case, and a board memo, then defend your work in a live industry panel review. Nine sector-specific themes to choose from.',
  },
  {
    q: 'Can my company sponsor me / a whole cohort?',
    a: 'Yes. We offer corporate cohort sponsorships with dedicated capstones aligned to your firm\u2019s AI roadmap. Talk to a program advisor and we\u2019ll walk you through the sponsor pathway.',
  },
  {
    q: 'How much does it cost?',
    a: 'We\u2019re holding pricing until we finalise founding-cohort partners. Express interest via the form or the advisor call and we\u2019ll share commercials one-on-one — plus available fellowships for high-potential candidates.',
  },
  {
    q: 'What if I miss a session?',
    a: 'Every session is recorded and shipped within 24 hours with a facilitator-led recap. Fast Track has a 90% attendance floor; Full-Time offers up to two make-up windows per semester.',
  },
  {
    q: 'Is this UGC-recognised?',
    a: 'The AI-Enabled MBA program itself is a hybrid learning credential. When co-issued with a UGC-recognised partner university, it carries the partner\u2019s regulatory recognition. Talk to us for details on institutional partnerships.',
  },
];

// ---------------------------------------------------------------------------
// Content data (from the AI-Enabled MBA Program Proposal by Vijay Karna)
// ---------------------------------------------------------------------------
const SECTIONS = [
  { id: 'overview',       label: 'Overview' },
  { id: 'why-now',        label: 'Why Now' },
  { id: 'formats',        label: 'Two Formats' },
  { id: 'format-fit',     label: 'Format Fit Quiz' },
  { id: 'curriculum',     label: 'Curriculum' },
  { id: 'modules',        label: '12 Modules' },
  { id: 'certifications', label: 'Global Certs' },
  { id: 'delivery',       label: 'Delivery' },
  { id: 'roles',          label: 'Ecosystem Roles' },
  { id: 'lab',            label: 'AI Business Lab' },
  { id: 'capstones',      label: 'Capstones' },
  { id: 'assessment',     label: 'Assessment' },
  { id: 'certification',  label: 'Certification' },
  { id: 'governance',     label: 'Governance' },
  { id: 'difference',     label: 'Difference' },
  { id: 'roadmap',        label: 'Launch' },
  { id: 'faq',            label: 'FAQ' },
  { id: 'enroll',         label: 'Express Interest' },
];

const MODULES = [
  { icon: Brain,       title: 'AI for Managers',                     brief: 'Concepts, limits & business relevance' },
  { icon: BarChart3,   title: 'Business Data & Decision Analytics',  brief: 'Descriptive · Predictive · Prescriptive' },
  { icon: ShoppingBag, title: 'AI for Marketing, Sales & CX',        brief: 'Segmentation · Personalization · Copilots' },
  { icon: IndianRupee, title: 'AI for Finance, Risk & Performance',  brief: 'Forecasting · Fraud · Scenario planning' },
  { icon: Users,       title: 'AI for HR, Talent & Future of Work',  brief: 'Skills · Learning · Ethical HR AI' },
  { icon: Truck,       title: 'AI for Operations, SCM & Service',    brief: 'Demand · Inventory · Automation · OEE' },
  { icon: Layers,      title: 'Digital Transformation & Enterprise Platforms', brief: 'ERP · CRM · SCM · HRMS · Cloud · APIs' },
  { icon: Bot,         title: 'Generative AI, Copilots & Prompt-to-Workflow', brief: 'GenAI · Document intel · Automation' },
  { icon: GitBranch,   title: 'Agentic AI & Business Process Orchestration', brief: 'Multi-agent · Human-in-loop · Autonomy' },
  { icon: ShieldCheck, title: 'Responsible AI, Cybersecurity & Governance', brief: 'Ethics · Privacy · Model risk · Audit' },
  { icon: Factory,     title: 'Industry AI & Sectoral Transformation', brief: 'Manufacturing · Energy · BFSI · Healthcare' },
  { icon: Trophy,      title: 'Business Value Realization & Capstone Studio', brief: 'ROI · Adoption · Board-ready story' },
];

const SEMESTERS = [
  { id: 1, stage: 'Foundation',              cluster: 'AI, Business & Data Foundations', focus: 'Management fundamentals, AI literacy, business statistics, data visualization, economics, accounting, responsible AI and problem framing.', output: 'Mini case: identify and quantify one AI opportunity in a business function.' },
  { id: 2, stage: 'Functional Application',  cluster: 'AI for Core Management Functions', focus: 'AI use cases in marketing, finance, HR, operations, supply chain, customer experience and business analytics.', output: 'Functional AI dashboard or workflow automation prototype.' },
  { id: 3, stage: 'Enterprise Transformation', cluster: 'Agentic AI, Digital Strategy & Industry Electives', focus: 'Digital transformation, enterprise architecture, process mining, agentic AI, AI governance, platforms, change management and industry tracks.', output: 'Enterprise AI transformation blueprint for a selected sector.' },
  { id: 4, stage: 'Capstone Professional',   cluster: 'AI Business Transformation Studio', focus: 'Live or simulated business problem, stakeholder discovery, prototype, business case, governance and adoption roadmap.', output: 'Final capstone demo, ROI model and board-ready management presentation.' },
];

const FAST_TRACK_WEEKS = [
  { r: '1-2',   focus: 'AI literacy & problem framing',       output: 'AI opportunity canvas for your business' },
  { r: '3-4',   focus: 'Data analytics & functional dashboards', output: 'Live KPI dashboard for your function' },
  { r: '5-6',   focus: 'GenAI, copilots & prompt-to-workflow', output: 'Working prompt library + automated workflow' },
  { r: '7-8',   focus: 'Agentic AI & enterprise integration',  output: 'Multi-agent process design + governance' },
  { r: '9-10',  focus: 'Sectoral & functional AI (elective)',  output: 'Sector-specific transformation blueprint' },
  { r: '11-12', focus: 'Capstone studio & board presentation', output: 'Board-ready AI business case + prototype' },
];

const ROLES_CARDS = [
  { icon: Compass,      title: 'Program Architects',   text: 'Design blueprint, module logic, learning outcomes, assessment model and approval-ready documentation.' },
  { icon: GraduationCap,title: 'Practitioner Faculty', text: 'Deliver modules, masterclasses, case clinics, capstone reviews and executive storytelling.' },
  { icon: Handshake,    title: 'Industry Mentors',     text: 'Convert 30+ years of industry experience into live cases, value models and transformation roadmaps.' },
  { icon: UserCog,      title: 'Faculty Coaches',      text: 'Enable internal faculty via teaching notes, AI labs, rubrics, cases and train-the-trainer workshops.' },
  { icon: Zap,          title: 'SAMANVAI Strategy Studio', text: 'AI-powered co-creation platform for company analysis, market intelligence and decision narratives.' },
  { icon: Building2,    title: 'Industry–Academia Advisors', text: 'Partner with enterprises, MSMEs, startups and tech providers for projects, internships and showcases.' },
  { icon: Trophy,       title: 'Capstone Jury Leads',  text: 'Guide student teams on problem framing, ROI, governance, adoption risks and board-level presentation.' },
];

const LAB_ELEMENTS = [
  { icon: Beaker,      title: 'Business AI Sandbox',            text: 'No-code AI · spreadsheet analytics · dashboards · GenAI prompting · document intelligence · workflow automation.' },
  { icon: BarChart3,   title: 'Business Dataset Library',       text: 'Sales · customer · HR · finance · operations · supply chain · service · ESG · market datasets for assignments and projects.' },
  { icon: Sparkles,    title: 'SAMANVAI Strategy Studio',       text: 'AI-powered company analysis · market intelligence · business value cases · executive storylines · decision support.' },
  { icon: Layers,      title: 'Enterprise Systems Exposure',    text: 'ERP · CRM · SCM · HRMS process maps · case datasets · workflow tools · integration concepts.' },
  { icon: ShieldCheck, title: 'Responsible AI Governance Kit',  text: 'Policies · model-risk checklist · human-approval gates · privacy templates · ethics rubric · audit trail examples.' },
  { icon: Trophy,      title: 'Capstone Studio Kit',            text: 'Problem briefs · stakeholder templates · ROI calculator · risk register · roadmap · board presentation format.' },
];

const CAPSTONES = [
  { theme: 'AI Strategy & Market Intelligence', problem: 'Analyze an industry, competitor set and target company to identify AI-led growth and productivity value pools.' },
  { theme: 'Sales & Customer Growth',           problem: 'Build a sales intelligence and customer segmentation model to improve conversion, retention or wallet share.' },
  { theme: 'Finance & Performance Management',  problem: 'Create a cost, cash or margin analytics dashboard with forecast scenarios and management actions.' },
  { theme: 'HR & Workforce Productivity',       problem: 'Design a skills intelligence model for talent matching, project allocation, reskilling and productivity.' },
  { theme: 'Supply Chain & Operations',         problem: 'Compress forecast-to-procure or order-to-deliver cycle time using AI agents and workflow automation.' },
  { theme: 'Manufacturing / Services Productivity', problem: 'Use operational data to identify bottlenecks, quality issues, downtime or service productivity levers.' },
  { theme: 'Responsible AI Governance',         problem: 'Develop an AI policy, model-risk framework and human-in-the-loop governance model for an enterprise.' },
  { theme: 'Startup / Innovation Studio',       problem: 'Create an AI venture concept with customer discovery, MVP plan, GTM, unit economics and investor pitch.' },
  { theme: 'ESG & Sustainability Analytics',    problem: 'Build an AI-supported ESG dashboard for energy, emissions, circularity, compliance or stakeholder reporting.' },
];

const ASSESSMENT = [
  { component: 'Concept readiness',      evidence: 'Quizzes, reflections, oral checks, short cases',                     weight: 15 },
  { component: 'Hands-on assignments',   evidence: 'Dashboard, AI prompt workflow, no-code model, analytics output',      weight: 25 },
  { component: 'Business case analysis', evidence: 'Problem framing, alternatives, risks, stakeholder impact, value logic', weight: 20 },
  { component: 'Mini-project / capstone',evidence: 'Prototype, business case, governance, implementation roadmap',        weight: 30 },
  { component: 'Executive presentation', evidence: 'ROI, benefits, adoption story, board-ready communication',            weight: 10 },
];

const CERT_LEVELS = [
  { level: 1, name: 'AI Business Explorer',            audience: 'MBA foundation students / newcomers',           capability: 'Understands AI, data, business functions, responsible AI and opportunity identification.' },
  { level: 2, name: 'Functional AI Builder',           audience: 'MBA second-semester / executives',              capability: 'Can build dashboards, prompt workflows and AI-supported functional use cases.' },
  { level: 3, name: 'Enterprise AI Practitioner',      audience: 'Advanced MBA / specialization students',        capability: 'Can connect AI to enterprise systems, governance, change and performance metrics.' },
  { level: 4, name: 'AI Business Transformation Professional', audience: 'Final semester / executive capstone',   capability: 'Delivers board-ready AI transformation with prototype, ROI and adoption roadmap.' },
];

const GOVERNANCE = [
  { area: 'Steering committee',        mechanism: 'Dean, MBA chair, program coordinator, Program Architect, selected faculty, placement lead and industry advisor.' },
  { area: 'Academic governance',       mechanism: 'Board of Studies review, credit mapping, CO/PO alignment, assessment policy, certification decision.' },
  { area: 'Delivery governance',       mechanism: 'Monthly review of schedule, student engagement, assignment completion, faculty adoption and capstone progress.' },
  { area: 'Ethics & Responsible AI',   mechanism: 'AI-use policy, disclosure rules, human-in-the-loop controls, data privacy and model-risk safeguards.' },
  { area: 'Quality metrics',           mechanism: 'Attendance, student satisfaction, assignment completion, capstone quality, faculty adoption and industry participation.' },
  { area: 'Outcome metrics',           mechanism: 'Projects completed, prototypes built, internships, placements, publications, competitions, incubation opportunities.' },
];

const DIFFERENCE = [
  { icon: Target,     title: 'Program identity',    text: 'Moves beyond generic AI awareness into function-wise business application, enterprise platforms, governance, adoption and quantified value creation.' },
  { icon: PlayCircle, title: 'Learning model',      text: 'Practitioner-led sessions, faculty co-teaching, AI labs, live business cases, capstone studios, industry jury reviews and placement-oriented portfolios.' },
  { icon: Building2,  title: 'Management value',    text: 'Credible, market-facing program aligned with employability, NEP-style flexibility, industry-academia collaboration and responsible transformation.' },
  { icon: Award,      title: 'Student value',       text: 'Graduates leave with a portfolio of AI-enabled business problem statements, prototypes, decision dashboards, business cases and board-ready presentations.' },
];

const ROADMAP = [
  { p: 'Phase 0', name: 'Management alignment',                dur: '1 week',       out: 'Decision on program route, pilot scope, target cohort, mode and approval owner.' },
  { p: 'Phase 1', name: 'Curriculum & regulatory discovery',   dur: '2 weeks',      out: 'Current-state review, approval map, AI-readiness gap and course positioning.' },
  { p: 'Phase 2', name: 'Program blueprint',                   dur: '3–4 weeks',    out: 'Semester-wise syllabus, modules, outcomes, contact hours, assessment and lab design.' },
  { p: 'Phase 3', name: 'Content & case-pack build',           dur: '6–8 weeks',    out: 'Slides, cases, datasets, lab guides, prompt library, templates and rubrics.' },
  { p: 'Phase 4', name: 'Pilot delivery',                      dur: '8–12 weeks',   out: 'Masterclasses, labs, assignments, student feedback and early showcase.' },
  { p: 'Phase 5', name: 'Scale & institutionalize',            dur: '1–2 semesters',out: 'Faculty enablement, capstone studio, industry partnerships, admissions narrative, quality dashboard.' },
];

const AUDIENCE = [
  { icon: Users,        title: 'Working professionals & managers',    tag: 'Fast Track' },
  { icon: Rocket,       title: 'Entrepreneurs & family-business heirs', tag: 'Both' },
  { icon: PenTool,      title: 'Consultants, freelancers & advisors', tag: 'Fast Track' },
  { icon: Building2,    title: 'Universities & business schools',     tag: 'Full-Time · Partnerships' },
  { icon: GraduationCap,title: 'MBA / PGDM students',                 tag: 'Full-Time' },
  { icon: UserCog,      title: 'Faculty & academic leaders',          tag: 'Both · Faculty Enablement' },
  { icon: Factory,      title: 'Corporate L&D + digital-transformation leaders', tag: 'Cohort partnerships' },
  { icon: Handshake,    title: 'Industry Professors of Practice',     tag: 'Teach · Co-create · Mentor' },
];

const LEARNER_TYPES = [
  ['working_professional', 'Working professional / manager'],
  ['manager',              'Function / team head'],
  ['entrepreneur',         'Entrepreneur / founder'],
  ['freelancer_consultant','Consultant / freelancer'],
  ['faculty',              'Faculty / academic'],
  ['industry_leader',      'Industry leader'],
  ['mba_student',          'MBA / PGDM student'],
  ['university_director',  'University / B-school leadership'],
  ['corporate_ld_head',    'Corporate L&D head'],
  ['other',                'Other'],
];

// ---------------------------------------------------------------------------
// Small building-blocks
// ---------------------------------------------------------------------------
const SectionEyebrow = ({ children }) => (
  <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">{children}</div>
);

const Section = ({ id, eyebrow, title, subtitle, children }) => (
  <section id={id} className="scroll-mt-24 py-14 border-b border-white/5">
    <div className="max-w-5xl">
      <SectionEyebrow>{eyebrow}</SectionEyebrow>
      <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-white">{title}</h2>
      {subtitle && <p className="text-sm text-white/65 mt-2 max-w-3xl">{subtitle}</p>}
    </div>
    <div className="mt-8">{children}</div>
  </section>
);

const StickyNav = () => {
  const [active, setActive] = useState('overview');
  useEffect(() => {
    const onScroll = () => {
      const midY = window.innerHeight / 3;
      let current = 'overview';
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top < midY) current = s.id;
      }
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <nav className="hidden lg:block sticky top-24 self-start w-56 flex-shrink-0" data-testid="mba-toc">
      <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 mb-3 pl-3">ON THIS PAGE</div>
      <ul className="space-y-0.5">
        {SECTIONS.map((s) => (
          <li key={s.id}>
            <a href={`#${s.id}`}
              data-testid={`mba-toc-${s.id}`}
              className={`block pl-3 pr-2 py-1 border-l-2 text-[12px] transition ${
                active === s.id ? 'border-amber-300 text-amber-200 font-semibold' : 'border-white/10 text-white/55 hover:text-white/85 hover:border-white/30'
              }`}>{s.label}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Books To Refer — 2 hand-picked books shown at the top-right of the MBA hero.
// Deep-links to /pop/books?book=<id> which auto-opens the detail modal there.
// ---------------------------------------------------------------------------
const BooksToRefer = () => {
  const [books, setBooks] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await popListBooks({ limit: 60 });
        if (!mounted) return;
        // Preserve REFERENCE_BOOK_TITLES order, first case-insensitive substring match wins.
        const picked = [];
        for (const needle of REFERENCE_BOOK_TITLES) {
          const b = (r.results || []).find((x) =>
            (x.title || '').toUpperCase().includes(needle) && !picked.some((p) => p.id === x.id)
          );
          if (b) picked.push(b);
        }
        setBooks(picked);
      } catch (_e) { /* silent — widget just hides */ }
      finally { if (mounted) setBusy(false); }
    })();
    return () => { mounted = false; };
  }, []);

  if (busy) return null;         // no flash while loading
  if (books.length === 0) return null;

  return (
    <div
      className="rounded-2xl border border-amber-300/60 bg-slate-900/40 px-4 pt-3 pb-4 mb-4 min-w-[280px] shadow-[0_0_0_1px_rgba(251,191,36,0.08)]"
      data-testid="mba-books-to-refer"
    >
      <div className="text-center font-serif text-lg text-white mb-3">Books To Refer</div>
      <div className="grid grid-cols-2 gap-3">
        {books.map((b) => (
          <Link
            key={b.id}
            to={`/pop/books?book=${encodeURIComponent(b.id)}`}
            data-testid={`mba-book-refer-${b.id}`}
            title={b.title}
            className="group block rounded-md overflow-hidden ring-1 ring-white/10 hover:ring-amber-300/60 transition"
          >
            {b.cover_url ? (
              <img
                src={popBookCoverUrl(b.id)}
                alt={b.title}
                loading="lazy"
                className="w-full h-40 object-cover group-hover:scale-[1.02] transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-40 flex flex-col items-center justify-center bg-slate-950 text-white/70 text-center p-2">
                <BookOpen className="w-6 h-6 text-amber-300 mb-2" />
                <span className="text-[10px] font-serif leading-tight line-clamp-3">{b.title}</span>
              </div>
            )}
          </Link>
        ))}
      </div>
      <Link
        to="/pop/books"
        data-testid="mba-books-to-refer-all"
        className="mt-3 flex items-center justify-center gap-1 text-[10px] tracking-[0.2em] uppercase font-bold text-amber-300 hover:text-amber-200"
      >
        Browse the full library <ChevronRight className="w-3 h-3" />
      </Link>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Interest form
// ---------------------------------------------------------------------------
const InterestForm = () => {
  const { user } = usePoPAuth();
  const [f, setF] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    learner_type: 'working_professional',
    format_preference: 'either',
    institution_or_org: '',
    role_title: '',
    location: '',
    goal: '',
    referral_source: '',
    consent_marketing: true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(null);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim()) { setErr('Name and email are required'); return; }
    setBusy(true); setErr('');
    try {
      const r = await popMBASubmitInterest(f);
      setOk(r.interest);
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not submit');
    } finally { setBusy(false); }
  };

  if (ok) {
    return (
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center" data-testid="mba-interest-success">
        <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
        <h3 className="font-serif text-2xl text-white mb-1">You&apos;re on the list.</h3>
        <p className="text-sm text-white/70 max-w-xl mx-auto">
          Reference <span className="font-mono text-emerald-200">{ok.id.slice(0, 8).toUpperCase()}</span>.
          We&apos;ll reach out to <span className="text-white/90 font-medium">{ok.email}</span> with next steps for the
          {' '}<span className="text-amber-200">{ok.format_preference === 'fast_track' ? '12-week Fast Track' : ok.format_preference === 'full_time' ? '24-month Full-Time' : 'AI-Enabled MBA'}</span> pathway.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-6 sm:p-8" data-testid="mba-interest-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={f.name} onChange={upd('name')} placeholder="Full name *" data-testid="mba-name"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <input type="email" value={f.email} onChange={upd('email')} placeholder="Email *" data-testid="mba-email"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <input value={f.phone} onChange={upd('phone')} placeholder="Phone / WhatsApp"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <select value={f.learner_type} onChange={upd('learner_type')} data-testid="mba-learner-type"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white">
          {LEARNER_TYPES.map(([id, lbl]) => (<option key={id} value={id} className="bg-slate-900">{lbl}</option>))}
        </select>
        <input value={f.institution_or_org} onChange={upd('institution_or_org')} placeholder="Institution / Organisation"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <input value={f.role_title} onChange={upd('role_title')} placeholder="Role / Title"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <input value={f.location} onChange={upd('location')} placeholder="Location (city, country)"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <select value={f.format_preference} onChange={upd('format_preference')} data-testid="mba-format-pref"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white">
          <option value="fast_track" className="bg-slate-900">Fast Track (12 weeks)</option>
          <option value="full_time"  className="bg-slate-900">Full-Time (24 months)</option>
          <option value="either"     className="bg-slate-900">Either / Not sure yet</option>
        </select>
      </div>
      <textarea rows={3} value={f.goal} onChange={upd('goal')} placeholder="What do you want to achieve? (optional)"
        className="mt-3 w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />

      {err && <div className="text-xs text-rose-300 mt-2">{err}</div>}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={submit} disabled={busy} data-testid="mba-submit"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-40 text-slate-900 text-sm font-semibold shadow-md">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Express interest
        </button>
        <span className="text-[10px] text-white/50">No fees or commercials at this stage · we&apos;ll send curriculum, cohort dates and next steps.</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Prospectus download button — MANDATORY personalisation via popover
// Collects recipient / email / phone before triggering a POST download.
// ---------------------------------------------------------------------------
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s\-().]{6,20}$/; // client-side loose match; backend re-validates

const ProspectusButton = ({ variant = 'hero', defaults = {} }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);
  const [form, setForm] = useState({
    recipient: defaults.recipient || '',
    email: defaults.email || '',
    phone: defaults.phone || '',
  });
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const upd = (k) => (e) => { setForm({ ...form, [k]: e.target.value.slice(0, k === 'recipient' ? 80 : 120) }); setOk(false); setErr(''); };

  const recipient = form.recipient.trim();
  const email = form.email.trim();
  const phone = form.phone.trim();
  const emailOk = EMAIL_RE.test(email);
  const phoneOk = PHONE_RE.test(phone);
  const ready = recipient.length >= 2 && emailOk && phoneOk;

  const heroCls = 'inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-slate-900 hover:bg-slate-800 border border-amber-300/40 hover:border-amber-300 text-amber-200 text-sm font-semibold shadow-md transition disabled:opacity-60';
  const compactCls = 'inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-xs font-semibold shadow-sm disabled:opacity-60';

  const doDownload = async () => {
    if (!ready || busy) { setOpen(true); return; }
    setBusy(true); setErr(''); setOk(false);
    try {
      await popMBADownloadProspectus({ recipient, email, phone });
      setOk(true);
      setTimeout(() => setOpen(false), 1400);
    } catch (e) {
      setErr(e?.message || 'Could not download prospectus');
    } finally {
      setBusy(false);
    }
  };

  const handleMainClick = (e) => {
    e.preventDefault();
    if (!ready) {
      setOpen(true);
      setTimeout(() => {
        // Focus the first empty field.
        const missing =
          recipient.length < 2 ? `mba-${variant}-personalise-input` :
          !emailOk ? `mba-${variant}-personalise-email` :
          `mba-${variant}-personalise-phone`;
        const el = wrapRef.current && wrapRef.current.querySelector(`[data-testid="${missing}"]`);
        if (el) el.focus();
      }, 60);
      return;
    }
    doDownload();
  };

  return (
    <div ref={wrapRef} className="relative inline-block">
      <div className="flex items-stretch gap-0">
        <button
          type="button"
          onClick={handleMainClick}
          disabled={busy}
          data-testid={`mba-${variant}-download-prospectus`}
          title={ready ? `Download prospectus personalised for ${recipient}` : 'Fill 3 quick details to unlock the download'}
          className={`${variant === 'hero' ? heroCls : compactCls} rounded-r-none pr-3`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Download prospectus (PDF)
        </button>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`mba-${variant}-personalise-toggle`}
          aria-label="Personalise prospectus for a recipient"
          title="Personalise this PDF"
          className={`${variant === 'hero' ? heroCls : compactCls} rounded-l-none border-l-0 px-2.5`}
        >
          <UserCog className="w-3.5 h-3.5" />
        </button>
      </div>

      {!ready && (
        <div className="text-[10px] text-amber-300/80 mt-1.5 flex items-center gap-1">
          <UserCog className="w-3 h-3" />
          Personalisation required — recipient, email & phone unlock the download.
        </div>
      )}

      {open && (
        <div
          data-testid={`mba-${variant}-personalise-popover`}
          className="absolute z-30 mt-2 w-[22rem] rounded-xl border border-white/15 bg-slate-950/95 backdrop-blur shadow-2xl p-4 right-0"
        >
          <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-1.5">PERSONALISE THIS PDF</div>
          <div className="text-[11px] text-white/60 leading-relaxed mb-3">
            We stamp the cover + Next Steps with your institution&apos;s name and log this
            download so we can follow up within hours. Your details stay with SAMANVAI.
          </div>

          <label className="block text-[10px] uppercase tracking-wider font-semibold text-white/50 mb-1">Recipient / Institution *</label>
          <input
            type="text"
            value={form.recipient}
            onChange={upd('recipient')}
            placeholder="e.g. IIM Bangalore  ·  Dr. Priya Sharma"
            data-testid={`mba-${variant}-personalise-input`}
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none"
          />
          <div className="mt-1 text-[10px] text-white/40 mb-3">{form.recipient.length}/80 characters</div>

          <label className="block text-[10px] uppercase tracking-wider font-semibold text-white/50 mb-1">Email *</label>
          <input
            type="email"
            value={form.email}
            onChange={upd('email')}
            placeholder="you@institution.edu"
            data-testid={`mba-${variant}-personalise-email`}
            className={`w-full px-3 py-2 rounded-md bg-white/5 border text-sm text-white placeholder:text-white/30 focus:outline-none ${
              form.email && !emailOk ? 'border-rose-400/60 focus:border-rose-300' : 'border-white/10 focus:border-amber-300/60'
            }`}
          />
          {form.email && !emailOk && (
            <div className="text-[10px] text-rose-300 mt-1">Please enter a valid email address.</div>
          )}

          <label className="block text-[10px] uppercase tracking-wider font-semibold text-white/50 mt-3 mb-1">Phone / WhatsApp *</label>
          <input
            type="tel"
            value={form.phone}
            onChange={upd('phone')}
            placeholder="+91 98765 43210"
            data-testid={`mba-${variant}-personalise-phone`}
            className={`w-full px-3 py-2 rounded-md bg-white/5 border text-sm text-white placeholder:text-white/30 focus:outline-none ${
              form.phone && !phoneOk ? 'border-rose-400/60 focus:border-rose-300' : 'border-white/10 focus:border-amber-300/60'
            }`}
          />
          {form.phone && !phoneOk && (
            <div className="text-[10px] text-rose-300 mt-1">Enter 6–20 digits (optional leading +).</div>
          )}

          {err && <div className="text-[11px] text-rose-300 mt-3" data-testid={`mba-${variant}-personalise-error`}>{err}</div>}
          {ok && <div className="text-[11px] text-emerald-300 mt-3 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Download started.</div>}

          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={doDownload}
              disabled={!ready || busy}
              data-testid={`mba-${variant}-personalise-download`}
              className={`flex-1 justify-center inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold shadow-sm transition ${
                ready && !busy
                  ? 'bg-amber-300 hover:bg-amber-200 text-slate-900'
                  : 'bg-white/10 text-white/40 cursor-not-allowed'
              }`}
            >
              {busy
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>
                : ready
                  ? <><Download className="w-3.5 h-3.5" /> {`Download for ${recipient.length > 20 ? recipient.slice(0, 20) + '…' : recipient}`}</>
                  : <><Download className="w-3.5 h-3.5" /> Fill the fields above</>}
            </button>
            <button
              type="button"
              onClick={() => { setForm({ recipient: '', email: '', phone: '' }); setErr(''); setOk(false); setOpen(false); }}
              className="px-2.5 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/70"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const PoPAIEnabledMBA = () => {
  const { user } = usePoPAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => { popMBAStats().then(setStats).catch(() => {}); }, []);

  // Prefill the download form when the visitor is signed in.
  const defaults = {
    recipient:
      (user && (user.institution_or_org || (['university_director', 'faculty'].includes(user.role) ? user.name : ''))) || '',
    email: (user && user.email) || '',
    phone: (user && (user.phone || user.whatsapp)) || '',
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <PoPShell activeTab="ai-mba">
      <div className="flex gap-10">
        <StickyNav />

        <div className="flex-1 min-w-0">
          {/* ============================================ HERO ============================================ */}
          <section id="overview" className="relative scroll-mt-24 pt-0 pb-14">
            <AnimatedGradientBg className="-z-10 -inset-x-8 -top-8 rounded-3xl" />
            <div className="grid lg:grid-cols-[1fr_auto] gap-10 items-start">
              <div>
                <Reveal delay={0}>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-300/40 bg-amber-300/10 mb-4">
                    <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                    <span className="text-[10px] tracking-[0.3em] font-bold text-amber-300">FLAGSHIP · PROFESSOR OF PRACTICE SUITE</span>
                  </div>
                </Reveal>
                <Reveal delay={80}>
                  <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white">
                    AI-Enabled <span className="text-amber-300">MBA</span>
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <div className="mt-3 text-lg sm:text-xl text-white/80 max-w-3xl italic">
                    From textbook management to AI-enabled, industry-led, experiential capability.
                  </div>
                </Reveal>
                <Reveal delay={220}>
                  <p className="mt-6 text-sm sm:text-base text-white/70 max-w-3xl leading-relaxed">
                    A hybrid learning program — deliverable online or in-classroom — that helps universities, working
                    professionals, entrepreneurs and industry leaders become AI-enabled management professionals with a
                    portfolio of prototypes, decision dashboards, business cases and board-ready presentations.
                  </p>
                </Reveal>

                <Reveal delay={300}>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <button onClick={() => scrollTo('formats')} data-testid="mba-hero-formats"
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold shadow-md">
                      Explore the two formats <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => scrollTo('enroll')} data-testid="mba-hero-enroll"
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold shadow-md">
                      Express interest <Send className="w-3.5 h-3.5" />
                    </button>
                    <ProspectusButton variant="hero" defaults={defaults} />
                    <button onClick={() => scrollTo('curriculum')}
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/85 text-sm font-semibold">
                      See curriculum
                    </button>
                  </div>
                </Reveal>

                {/* Cohort countdown */}
                <Reveal delay={380}>
                  <div className="mt-8 max-w-md">
                    <CohortCountdown targetIso={NEXT_COHORT_ISO} label="Next Fast Track cohort begins in" />
                  </div>
                </Reveal>
              </div>

              {/* Right column: Books To Refer + Vitals card */}
              <Reveal delay={450}>
                <div className="min-w-[280px] max-w-sm">
                  <BooksToRefer />

                  {/* Vitals card */}
                  <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-6">
                    <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-3">PROGRAM VITALS</div>
                    <div className="space-y-3">
                      {[
                        { icon: Timer,        label: 'Fast track',       value: '12 weeks' },
                        { icon: Calendar,     label: 'Full-time',        value: '24 months · 4 semesters' },
                        { icon: BookOpen,     label: 'Core modules',     value: '12 · practice-driven' },
                        { icon: Beaker,       label: 'Capstone themes',  value: '9 · industry-briefed' },
                        { icon: Award,        label: 'Certification',    value: '4-level ladder' },
                        { icon: Layers,       label: 'Delivery',         value: 'Online · Hybrid · Classroom' },
                      ].map((v) => (
                        <div key={v.label} className="flex items-center gap-2.5 text-xs">
                          <v.icon className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
                          <span className="text-white/50 uppercase tracking-wider text-[9px] w-20">{v.label}</span>
                          <span className="text-white/90 font-semibold flex-1">{v.value}</span>
                        </div>
                      ))}
                    </div>
                    {stats && (
                      <div className="mt-5 pt-4 border-t border-white/10 text-center">
                        <div className="text-3xl font-serif font-bold text-amber-300 tabular-nums leading-none">
                          <CountUp value={stats.total_interest || 0} data-testid="mba-interest-counter" />
                        </div>
                        <div className="text-[10px] tracking-wider uppercase text-white/50 mt-1">Interest registrations so far</div>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Executive summary blockquote */}
            <div className="mt-12 rounded-xl border-l-4 border-amber-300 bg-amber-300/[0.04] px-6 py-5">
              <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">EXECUTIVE SUMMARY</div>
              <p className="text-sm sm:text-base text-white/85 leading-relaxed max-w-4xl">
                A management program that converts a conventional MBA into an <b className="text-white">applied AI,
                digital transformation and business value</b> capability pathway — <i>academically credible,
                industry-relevant and institutionally approvable</i> — for deans, faculty, industry partners, and
                learners who want to lead the AI-enabled enterprise.
              </p>
            </div>

            {/* Audience strip */}
            <div className="mt-10">
              <div className="text-[10px] tracking-[0.3em] font-bold text-white/50 mb-3">BUILT FOR</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {AUDIENCE.map((a) => (
                  <div key={a.title} className="rounded-lg border border-white/10 bg-slate-900/40 p-3 hover:border-amber-300/30 transition">
                    <a.icon className="w-4 h-4 text-amber-300 mb-1.5" />
                    <div className="text-xs font-semibold text-white leading-tight">{a.title}</div>
                    <div className="text-[10px] text-white/50 mt-1">{a.tag}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ============================================ WHY NOW ============================================ */}
          <Section id="why-now" eyebrow="WHY NOW"
            title="AI has left the sandbox. Management education must catch up."
            subtitle="AI is reshaping every function — marketing, finance, HR, ops, supply chain, service. Yet MBA graduates arrive at the workplace with either abstract AI awareness or fragmented certificate learning. The AI-Enabled MBA closes that gap.">
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { icon: Lightbulb,  h: 'Beyond hype into practice',    b: 'Function-wise application, enterprise platforms, governance and quantified value creation — not just GenAI awareness.' },
                { icon: LineChart,  h: 'Portfolio, not just a degree', b: 'Learners graduate with prototypes, dashboards, business cases and board-ready presentations they can showcase.' },
                { icon: Building2,  h: 'Approvable & credible',        b: 'Aligned to university, UGC, AICTE and UGC-DEB approval routes; NEP-flexible; industry-jury reviewed.' },
              ].map((c) => (
                <div key={c.h} className="rounded-xl border border-white/10 bg-slate-900/40 p-5">
                  <c.icon className="w-5 h-5 text-amber-300 mb-3" />
                  <div className="font-serif text-lg font-semibold text-white leading-tight mb-1.5">{c.h}</div>
                  <div className="text-xs text-white/70 leading-relaxed">{c.b}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-xl border border-white/10 bg-slate-900/30 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-amber-300" />
                <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300">FIT INSIDE THE PROFESSOR OF PRACTICE SUITE</div>
              </div>
              <p className="text-sm text-white/80 leading-relaxed">
                AI-Enabled MBA sits alongside <b>PoP Directory</b>, <b>Opportunities</b>, <b>Mentorship</b>,
                {' '}<b>Curriculum Gap Board</b>, <b>Q&amp;A</b>, <b>Books Marketplace</b> and <b>Case Library</b>. Every
                other portal capability feeds this program: verified experts teach, faculty co-design, universities
                sponsor cohorts, industry contributes cases, and students showcase outcomes — one integrated
                confluence of industry and academia.
              </p>
            </div>
          </Section>

          {/* ============================================ TWO FORMATS ============================================ */}
          <Section id="formats" eyebrow="TWO FORMATS · ONE FLAGSHIP"
            title="Choose your pathway."
            subtitle="Both formats share the same practice-driven DNA — 12 modules, capstone studio, industry jury, and a 4-level certification ladder.">
            <div className="grid lg:grid-cols-2 gap-5">
              {/* Fast track */}
              <div className="rounded-2xl border border-amber-300/40 bg-gradient-to-br from-amber-300/[0.08] via-slate-900/40 to-slate-900/40 p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-amber-300/20 blur-2xl" />
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-300 text-slate-900 text-[10px] tracking-wider uppercase font-bold mb-3">
                  <Timer className="w-3 h-3" /> 12 Weeks · Fast Track
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight">Become AI-enabled in one quarter.</h3>
                <p className="text-sm text-white/75 mt-2">For working professionals, managers, entrepreneurs, freelancers, consultants, faculty and industry leaders who need to act fast without leaving their day job.</p>

                <div className="mt-5 space-y-2">
                  {FAST_TRACK_WEEKS.map((w) => (
                    <div key={w.r} className="grid grid-cols-[56px_1fr] gap-3 items-start text-sm">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300 tabular-nums pt-0.5">Wk {w.r}</div>
                      <div>
                        <div className="text-white/90 font-medium leading-tight">{w.focus}</div>
                        <div className="text-[11px] text-white/55 mt-0.5">→ {w.output}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="text-[10px] tracking-[0.3em] font-bold text-white/50 mb-2">FORMAT SIGNALS</div>
                  <div className="flex flex-wrap gap-1.5">
                    {['Evenings & weekends', 'Live cohort', 'Career-portfolio outcome', 'Board-ready capstone'].map((t) => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/75">{t}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Full-time */}
              <div className="rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/[0.06] via-slate-900/40 to-slate-900/40 p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-emerald-400/20 blur-2xl" />
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10px] tracking-wider uppercase font-bold mb-3">
                  <Calendar className="w-3 h-3" /> 24 Months · Full-Time
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight">A two-year MBA, re-engineered for the AI enterprise.</h3>
                <p className="text-sm text-white/75 mt-2">For MBA students, business schools, and universities creating future-ready graduates. Four semesters — Foundation → Functional → Enterprise → Capstone.</p>

                <div className="mt-5 space-y-3">
                  {SEMESTERS.map((s) => (
                    <div key={s.id} className="rounded-lg border border-white/10 bg-slate-950/40 p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-300">Sem {s.id} · {s.stage}</div>
                      </div>
                      <div className="text-sm text-white/90 font-medium mt-1 leading-tight">{s.cluster}</div>
                      <div className="text-[11px] text-white/60 mt-1 leading-relaxed line-clamp-2">{s.focus}</div>
                      <div className="text-[10px] text-emerald-200/90 mt-1.5 flex items-start gap-1"><ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" /> {s.output}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="text-[10px] tracking-[0.3em] font-bold text-white/50 mb-2">FORMAT SIGNALS</div>
                  <div className="flex flex-wrap gap-1.5">
                    {['Credit-bearing', 'Board of Studies aligned', 'Industry jury reviews', 'Internships & placements'].map((t) => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/75">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* ============================================ FORMAT-FIT QUIZ ============================================ */}
          <Section id="format-fit" eyebrow="WHICH FORMAT FITS ME?"
            title="A 30-second decision helper."
            subtitle="Three quick questions. Get a personalised Fast Track vs Full-Time recommendation based on your time, urgency and career goals.">
            <FormatFitQuiz onCTAClick={() => scrollTo('enroll')} />
          </Section>

          {/* ============================================ CURRICULUM ARCHITECTURE ============================================ */}
          <Section id="curriculum" eyebrow="CURRICULUM ARCHITECTURE"
            title="Four stages. One transformation arc."
            subtitle="The same architecture is compressed into 12 weeks for Fast Track, and paced across four semesters for the Full-Time program.">
            <div className="grid md:grid-cols-4 gap-3">
              {SEMESTERS.map((s) => (
                <div key={s.id} className="rounded-xl border border-white/10 bg-slate-900/40 p-5 relative">
                  <div className="absolute -top-3 left-4 px-2 py-0.5 rounded-full bg-amber-300 text-slate-900 text-[10px] tracking-wider uppercase font-bold">Stage {s.id}</div>
                  <div className="mt-2 font-serif text-lg font-semibold text-white leading-tight">{s.stage}</div>
                  <div className="text-[11px] text-amber-300/90 tracking-wider uppercase mt-1">{s.cluster}</div>
                  <p className="text-xs text-white/70 leading-relaxed mt-3">{s.focus}</p>
                  <div className="mt-3 pt-3 border-t border-white/10 text-[11px] text-white/80">
                    <span className="text-emerald-300 font-bold">Output ·</span> {s.output}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* ============================================ MODULES ============================================ */}
          <Section id="modules" eyebrow="12 CORE MODULES"
            title="Function-wise AI. Not vibes."
            subtitle="Click any module to see learning outcomes, hours and the capstone brief. Filter by track to see how the arc unfolds across Foundation → Functional → Enterprise → Capstone.">
            <CurriculumExplorer />
          </Section>

          {/* ============================================ CERTIFICATIONS ============================================ */}
          <Section id="certifications" eyebrow="GLOBAL CERTIFICATION ALIGNMENT"
            title="One MBA. Eight bodies of knowledge. A shelf of portable credentials."
            subtitle="Every module maps to a globally-recognised professional body — PMI (Project Management), IIBA (Business Analysis), ICMCI (Management Consulting), IAPP & ISACA (AI Governance), ASCM/APICS (Supply Chain), CFA (Finance), SHRM/CIPD (HR) and Prosci/APMG (Change). Graduate exam-ready for the credentials that fit your career pathway.">
            <CertificationAlignment />
          </Section>

          {/* ============================================ DELIVERY ============================================ */}
          <Section id="delivery" eyebrow="HYBRID DELIVERY"
            title="Same content. Multiple depth-of-engagement modes."
            subtitle="From a 2-hour dean orientation to a 12-16 week capstone studio and a 6-12 month AI Business CoE advisory — pick the mode that fits your institution, cohort or team.">
            <div className="rounded-xl border border-white/10 bg-slate-900/40 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-white/50">
                  <tr>
                    <th className="text-left px-4 py-3">Format</th>
                    <th className="text-left px-4 py-3">Typical duration</th>
                    <th className="text-left px-4 py-3">Use case</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {[
                    { f: 'Leadership orientation', d: '2–3 hours',   u: 'Dean, program chair, faculty and placement team alignment.' },
                    { f: 'Foundation masterclass', d: '6–8 hours',   u: 'Awareness for MBA first-year and cross-disciplinary cohorts.' },
                    { f: 'Semester elective',      d: '30–40 hours', u: 'Credit-bearing elective with assignments and mini-project.' },
                    { f: 'Certification track',    d: '60–90 hours', u: 'Structured learning plus labs, reviews and micro-credential support.' },
                    { f: 'Capstone studio',        d: '12–16 weeks', u: 'Team-based live/simulated project, mentor reviews, final industry jury.' },
                    { f: 'Faculty enablement',     d: '2–5 days',    u: 'Train-the-trainer, case facilitation toolkit and assessment rubric handover.' },
                    { f: 'AI Business CoE advisory', d: '6–12 months', u: 'Program governance, industry connect, lab blueprint, internships, showcases.' },
                  ].map((r) => (
                    <tr key={r.f} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-white/90 font-medium">{r.f}</td>
                      <td className="px-4 py-3 text-amber-300 tabular-nums">{r.d}</td>
                      <td className="px-4 py-3 text-white/70">{r.u}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-white/50 mt-3 italic">In-person, hybrid or blended depending on institutional policy and statutory rules applicable to the program.</p>
          </Section>

          {/* ============================================ ROLES ============================================ */}
          <Section id="roles" eyebrow="ECOSYSTEM ROLES"
            title="Everyone contributes. Everyone learns."
            subtitle="Professors of Practice teach, mentor and jury; academicians co-create and co-teach; industry professionals contribute cases and internships; students build portfolios; universities and corporates sponsor cohorts.">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {ROLES_CARDS.map((r) => (
                <div key={r.title} className="rounded-xl border border-white/10 bg-slate-900/40 p-5">
                  <r.icon className="w-4 h-4 text-amber-300 mb-2" />
                  <div className="font-serif text-base font-semibold text-white leading-tight">{r.title}</div>
                  <div className="text-xs text-white/70 mt-1.5 leading-relaxed">{r.text}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/10 bg-slate-950 p-5">
                <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">STAKEHOLDERS CAN</div>
                <ul className="space-y-1.5 text-sm text-white/85">
                  {['Teach a module or masterclass', 'Enroll as a learner (fast track or full-time)', 'Mentor students through the capstone studio', 'Publish case studies with industry partners', 'Run live projects & internships', 'Co-create learning content on SAMANVAI'].map((t) => (
                    <li key={t} className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" /> {t}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-white/10 bg-slate-950 p-5">
                <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">CAREER PATHWAYS</div>
                <ul className="space-y-1.5 text-sm text-white/85">
                  {['AI Product Manager', 'Digital Transformation Lead', 'AI Business Analyst / Strategist', 'Responsible AI Governance Officer', 'Enterprise AI Consultant', 'AI-Enabled Function Head (Marketing / Finance / HR / Ops)', 'AI Startup Founder'].map((t) => (
                    <li key={t} className="flex items-start gap-2"><ChevronRight className="w-3.5 h-3.5 text-amber-300 mt-0.5 flex-shrink-0" /> {t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>

          {/* ============================================ AI LAB ============================================ */}
          <Section id="lab" eyebrow="AI BUSINESS LAB"
            title="Practical infrastructure, not expensive tech."
            subtitle="Six practical building blocks that scale from a pilot Business AI Sandbox to a full AI Business Centre of Excellence.">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {LAB_ELEMENTS.map((l) => (
                <div key={l.title} className="rounded-xl border border-white/10 bg-slate-900/40 p-5">
                  <l.icon className="w-4 h-4 text-amber-300 mb-2" />
                  <div className="font-serif text-base font-semibold text-white leading-tight">{l.title}</div>
                  <div className="text-xs text-white/70 mt-1.5 leading-relaxed">{l.text}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-white/50 mt-4 italic max-w-3xl">
              Institutions may use SAMANVAI, public AI tools, enterprise platforms, simulation tools, or selected
              technology partners. Final platform selection, licensing and data controls follow institutional
              procurement and IT policy.
            </p>
          </Section>

          {/* ============================================ CAPSTONES ============================================ */}
          <Section id="capstones" eyebrow="CAPSTONE PROJECTS"
            title="Nine industry-briefed themes."
            subtitle="Learners choose an industry-authored problem statement and deliver a board-ready capstone — prototype, ROI model, governance plan and adoption roadmap.">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {CAPSTONES.map((c) => (
                <div key={c.theme} className="rounded-xl border border-white/10 bg-slate-900/40 p-5 hover:border-emerald-400/30 transition">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-300 mb-1.5">Capstone</div>
                  <div className="font-serif text-base font-semibold text-white leading-tight">{c.theme}</div>
                  <div className="text-xs text-white/70 mt-2 leading-relaxed italic">&ldquo;{c.problem}&rdquo;</div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">SAMPLE CAPSTONE STUDIOS</div>
              <div className="font-serif text-2xl text-white mb-4">Real briefs from real sponsors.</div>
              <CapstoneGallery />
            </div>
          </Section>

          {/* ============================================ ASSESSMENT ============================================ */}
          <Section id="assessment" eyebrow="ASSESSMENT"
            title="Evidence beats exams."
            subtitle="Five weighted components — with 60% of the grade coming from applied work: assignments, business case, capstone and executive presentation.">
            <div className="space-y-2">
              {ASSESSMENT.map((a) => (
                <div key={a.component} className="grid sm:grid-cols-[1fr_2fr_120px] gap-3 items-center rounded-lg border border-white/10 bg-slate-900/40 p-4">
                  <div>
                    <div className="text-sm font-serif font-semibold text-white leading-tight">{a.component}</div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-300 mt-0.5">Component</div>
                  </div>
                  <div className="text-xs text-white/70 leading-relaxed">{a.evidence}</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-amber-300 to-emerald-400" style={{ width: `${a.weight * 3.33}%` }} />
                    </div>
                    <span className="text-amber-300 font-bold text-sm tabular-nums w-9 text-right">{a.weight}%</span>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* ============================================ CERTIFICATION ============================================ */}
          <Section id="certification" eyebrow="CERTIFICATION"
            title="A four-level capability ladder."
            subtitle="Each level certifies a capability — not just attendance. Learners can start at Level 1 and progress to Level 4, or enter at a higher level based on prior experience.">
            <div className="relative">
              <div className="absolute left-4 top-6 bottom-6 w-0.5 bg-gradient-to-b from-amber-300 via-emerald-400 to-emerald-500 hidden sm:block" />
              <div className="space-y-4">
                {CERT_LEVELS.map((c) => (
                  <div key={c.level} className="relative sm:pl-12">
                    <div className="hidden sm:flex absolute left-0 top-2 w-8 h-8 rounded-full bg-slate-950 border-2 border-amber-300 items-center justify-center text-amber-300 font-serif font-bold tabular-nums">{c.level}</div>
                    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5">
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                        <div className="font-serif text-lg font-semibold text-white">Level {c.level} · {c.name}</div>
                        <span className="text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded">{c.audience}</span>
                      </div>
                      <div className="text-xs text-white/70 leading-relaxed">{c.capability}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          {/* ============================================ GOVERNANCE ============================================ */}
          <Section id="governance" eyebrow="GOVERNANCE"
            title="Approvable. Auditable. Institutionalizable."
            subtitle="Six governance areas keep the program aligned with university policy, UGC/AICTE norms, ethics, and quality metrics.">
            <div className="grid sm:grid-cols-2 gap-3">
              {GOVERNANCE.map((g) => (
                <div key={g.area} className="rounded-xl border border-white/10 bg-slate-900/40 p-5">
                  <ClipboardCheck className="w-4 h-4 text-amber-300 mb-2" />
                  <div className="font-serif text-base font-semibold text-white leading-tight">{g.area}</div>
                  <div className="text-xs text-white/70 mt-1.5 leading-relaxed">{g.mechanism}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* ============================================ DIFFERENCE ============================================ */}
          <Section id="difference" eyebrow="DIFFERENTIATION"
            title="Not another AI certificate. Not a repackaged MBA."
            subtitle="How the AI-Enabled MBA is different from traditional MBAs, online MBAs, executive MBAs and standalone AI certificates.">
            <div className="grid sm:grid-cols-2 gap-3">
              {DIFFERENCE.map((d) => (
                <div key={d.title} className="rounded-xl border border-white/10 bg-slate-900/40 p-5">
                  <d.icon className="w-4 h-4 text-amber-300 mb-2" />
                  <div className="font-serif text-base font-semibold text-white leading-tight">{d.title}</div>
                  <div className="text-xs text-white/70 mt-1.5 leading-relaxed">{d.text}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* ============================================ ROADMAP ============================================ */}
          <Section id="roadmap" eyebrow="LAUNCH ROADMAP"
            title="From alignment to cohort in one semester."
            subtitle="Six phases from management alignment to institutionalized delivery. Ideal for a pilot cohort in the next academic term.">
            <div className="space-y-2">
              {ROADMAP.map((r) => (
                <div key={r.p} className="grid sm:grid-cols-[80px_1fr_140px] gap-3 rounded-lg border border-white/10 bg-slate-900/40 p-4 items-center">
                  <div>
                    <div className="text-[10px] tracking-wider uppercase font-bold text-amber-300">{r.p}</div>
                    <div className="text-[10px] text-white/50">{r.dur}</div>
                  </div>
                  <div>
                    <div className="text-sm font-serif font-semibold text-white">{r.name}</div>
                    <div className="text-xs text-white/70 mt-0.5">{r.out}</div>
                  </div>
                  <div className="hidden sm:flex justify-end">
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* ============================================ FAQ ============================================ */}
          <Section id="faq" eyebrow="FREQUENTLY ASKED"
            title="Everything applicants and universities ask us."
            subtitle="If your question isn&apos;t here, click the &ldquo;Talk to a program advisor&rdquo; button at the bottom-right.">
            <FAQAccordion items={MBA_FAQ} initialOpen={0} />
          </Section>

          {/* ============================================ ENROLL ============================================ */}
          <Section id="enroll" eyebrow="EXPRESS INTEREST"
            title="Join the founding cohort."
            subtitle="Three quick steps. We'll get back with cohort dates, curriculum, and next steps. No commercials at this stage.">
            <MultiStepInterest />

            <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-amber-300/30 bg-amber-300/[0.04] px-5 py-4">
              <FileText className="w-4 h-4 text-amber-300 flex-shrink-0" />
              <div className="flex-1 min-w-[220px]">
                <div className="text-sm font-serif font-semibold text-white leading-tight">Take the prospectus back to your team.</div>
                <div className="text-[11px] text-white/60 mt-0.5">
                  A concise, board-ready PDF brochure — personalise it with your institution&apos;s name to unlock the download.
                </div>
              </div>
              <ProspectusButton variant="enroll" defaults={defaults} />
            </div>
            <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
              {[
                { icon: MessagesSquare, h: 'Universities',    t: 'Discuss institutional partnerships, credit mapping and faculty enablement.' },
                { icon: Handshake,      h: 'Industry partners', t: 'Sponsor a cohort, contribute cases, or run capstone studios with your team.' },
                { icon: GraduationCap,  h: 'Learners',        t: 'Choose fast-track or full-time — we\'ll invite you to the next intake.' },
              ].map((c) => (
                <div key={c.h} className="rounded-lg border border-white/10 bg-slate-950 p-4">
                  <c.icon className="w-4 h-4 text-amber-300 mb-2" />
                  <div className="text-sm font-serif font-semibold text-white">{c.h}</div>
                  <div className="text-[11px] text-white/60 mt-1">{c.t}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* ============================================ TAGLINE FOOT ============================================ */}
          <div className="mt-8 rounded-xl border border-amber-300/30 bg-gradient-to-r from-amber-300/[0.06] via-transparent to-emerald-500/[0.06] px-6 py-6 text-center">
            <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-1">POSITIONING STATEMENT</div>
            <div className="font-serif text-lg sm:text-xl text-white leading-relaxed max-w-3xl mx-auto">
              &ldquo;<span className="text-amber-200">AI-Enabled MBA</span> — Learn AI for business impact.
              A practice-driven MBA specialization in <span className="text-white/85">Digital Transformation,
              Agentic AI and Business Value Management</span>.&rdquo;
            </div>
          </div>
        </div>
      </div>

      <AdvisorFAB program="ai-mba" defaults={{ name: user?.name || '', email: user?.email || '', phone: user?.phone || '' }} />
    </PoPShell>
  );
};

export default PoPAIEnabledMBA;
